import { Box, Typography } from '@mui/material';
import { comingSoonStyle, membershipStyles } from '../../themes/commonStyle';
import { MembershipType } from '../../payWall/paywall.model';

export type AudienceSectionPosition = 'left' | 'right';

type AudienceSectionProps = {
  image: string;
  index: number;
  title: string;
  description: string;
  position?: AudienceSectionPosition;
};

// Define marker tiers and their styles
const markerTiers: Record<string, { membership: MembershipType; comingSoon?: boolean }> = {
  'Median Rental Price': { membership: 'Free' },
  'Median Sale Price': { membership: 'Free' },
  'Median Rental Price per Sq Ft': { membership: 'Free' },
  'Median Sale Price per Sq Ft': { membership: 'Free' },
  'Days on Rental Market': { membership: 'Free' },
  'Days on Sales Market': { membership: 'Free' },
  'Number of Rentals': { membership: 'Free' },
  'Number of Sales': { membership: 'Free' },
  'Rent-on-Sale': { membership: 'Novice' },
  'Rental Appreciation': { membership: 'Novice' },
  'Sales Appreciation': { membership: 'Novice' },
  'Sales Market Efficiency': { membership: 'Novice' },
  'Rental Market Efficiency': { membership: 'Novice' },
  'Average Tax Rate': { membership: 'Pro' },
  'Cap Rate': { membership: 'Pro', comingSoon: true },
  'Median HOA': { membership: 'Pro', comingSoon: true },
  'Sales Price Prediction': { membership: 'Pro', comingSoon: true },
  'Rental Price Prediction': { membership: 'Pro', comingSoon: true }
};

// Sort markers by length descending to match longer markers first
const markers = Object.keys(markerTiers).sort((a, b) => b.length - a.length);

export const AudienceSection = ({ image, index, title, description, position = 'left' }: AudienceSectionProps) => {
  const renderDescription = (description: string) => {
    if (!markers.length) {
      return (
        <Typography sx={styles.sectionBody} gutterBottom>
          {description}
        </Typography>
      );
    }

    // Build a regex to match all markers, prioritizing longer ones
    const markerRegex = new RegExp(
      `(${markers.map(m => m.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
      'g'
    );
    const parts = description.split(markerRegex);

    return (
      <Typography sx={styles.sectionBody} gutterBottom>
        {parts.map((part, idx) =>
          markers.includes(part) ? (
            <span key={idx} style={membershipStyles[markerTiers[part].membership]}>
              {part}
              {markerTiers[part].comingSoon && (
                <span style={comingSoonStyle}> (Coming Soon)</span>
              )}
            </span>
          ) : (
            <span key={idx}>{part}</span>
          )
        )}
      </Typography>
    );
  };

  return (
    <Box display="flex" flexDirection={position === 'left' ? 'row' : 'row-reverse'} gap={4}>
      <Box flex='1' flexBasis='50%' justifyContent={'center'} display='flex'>
        <img src={image} style={styles.sectionImage} />
      </Box>
      <Box flex='1' flexBasis='50%' sx={{ display: 'flex' }} justifyContent={'left'}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 1200 }}>
          <Typography sx={styles.sectionTitle} gutterBottom>{`${index + 1}. ${title}`}</Typography>
          {renderDescription(description)}
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  sectionImage: {
    height: 400,
    objectFit: 'cover' as 'cover',
    objectPosition: 'center',
  },
  sectionTitle: {
    typography: 'h5',
    textAlign: 'left',
  },
  sectionBody: {
    typography: 'body1',
    textAlign: 'left',
  },
};
